import React from "react";
import { Link } from "react-router-dom";
import { Music, Megaphone, TrendingUp, ArrowRight, Users, Radio, Sparkles, Flame, Headphones } from "lucide-react";

/**
 * Communities — community hub that surfaces every audience and surface
 * within 1o1sound. Three lanes:
 *   - For artists  (upload, claim, badge)
 *   - For brands   (promo / advertising)
 *   - For backers  (investors)
 *
 * Brutalist editorial layout matching About / Jobs / Investors.
 */

const Kicker = ({ num, label }) => (
  <p className="text-[11px] uppercase tracking-[0.28em] font-black mb-3 flex items-center gap-2">
    <span className="text-vermilion">{num}</span>
    <span className="text-white/40">{label}</span>
  </p>
);

const LaneCard = ({ to, num, icon: Icon, kicker, title, body, cta, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group relative block bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-vermilion/50 rounded-sm p-7 transition overflow-hidden"
  >
    {/* Vermilion diagonal stripe accent on hover */}
    <div
      aria-hidden
      className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rotate-12 opacity-0 group-hover:opacity-20 transition-opacity"
      style={{ background: "repeating-linear-gradient(45deg, #E84525 0 4px, transparent 4px 10px)" }}
    />
    <div className="flex items-center justify-between mb-4">
      <span className="text-[10px] tracking-[0.28em] font-black text-vermilion">{num}</span>
      <Icon size={22} className="text-white/80 group-hover:text-vermilion transition" strokeWidth={2} />
    </div>
    <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/40 mb-1.5">{kicker}</p>
    <h3 className="font-display text-2xl font-black uppercase tracking-tight leading-tight mb-2">{title}</h3>
    <p className="text-sm text-white/55 leading-relaxed mb-5">{body}</p>
    <p className="text-vermilion text-[12px] font-black uppercase tracking-[0.18em] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
      {cta} <ArrowRight size={14} />
    </p>
  </Link>
);

const Communities = () => {
  return (
    <div data-testid="communities-page" className="relative">
      {/* Hero */}
      <section className="relative pt-6 pb-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-2 right-0 w-[380px] h-[380px] opacity-20 rotate-12"
          style={{ background: "repeating-linear-gradient(45deg, #E84525 0 6px, transparent 6px 14px)" }}
        />
        <Kicker num="01" label="Communities" />
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.92] mb-5 max-w-4xl">
          Three rooms.<br />One <span className="text-vermilion">platform</span>.
        </h1>
        <p className="text-base sm:text-lg text-white/65 leading-relaxed max-w-2xl">
          1o1sound is built for three audiences at once — the artists making the
          music, the brands amplifying it, and the backers funding the
          independence. Pick a door.
        </p>
      </section>

      {/* Three lanes */}
      <section className="mb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <LaneCard
            to="/upload"
            testId="communities-lane-artists"
            num="01"
            icon={Music}
            kicker="For artists"
            title="Artists"
            body="Upload MP3s + artwork, claim your iTunes profile, run promo campaigns, get paid 90/10. Three months free on Artist Premium."
            cta="Become an artist"
          />
          <LaneCard
            to="/promote"
            testId="communities-lane-advertising"
            num="02"
            icon={Megaphone}
            kicker="For brands & artists"
            title="Advertising"
            body="HMAC-signed promo campaigns place your track in front of the right listeners. Budget per impression — no minimums, no bidding wars."
            cta="Start a campaign"
          />
          <LaneCard
            to="/investors"
            testId="communities-lane-investors"
            num="03"
            icon={TrendingUp}
            kicker="For backers"
            title="Become an Investor"
            body="Back the streaming platform that pays artists 90¢ on the dollar. SAFE round open to angels, funds and strategic LPs."
            cta="See the thesis"
          />
        </div>
      </section>

      {/* Numbers strip */}
      <section className="border-y border-white/8 py-10 mb-20">
        <Kicker num="02" label="By the numbers" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10">
          <div>
            <p className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-none text-vermilion">90/10</p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-black mt-2">Revenue split</p>
            <p className="text-xs text-white/60 mt-1">Artist-favoured by default</p>
          </div>
          <div>
            <p className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-none text-vermilion">$0.18</p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-black mt-2">Net per stream</p>
            <p className="text-xs text-white/60 mt-1">Vs. ~$0.003 at majors</p>
          </div>
          <div>
            <p className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-none text-vermilion">3 mo</p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-black mt-2">Artist trial</p>
            <p className="text-xs text-white/60 mt-1">Free, no card</p>
          </div>
          <div>
            <p className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-none text-vermilion">$10</p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-black mt-2">Listener Premium</p>
            <p className="text-xs text-white/60 mt-1">2 months free</p>
          </div>
        </div>
      </section>

      {/* Adjacent surfaces */}
      <section className="mb-20">
        <Kicker num="03" label="Other ways to join" />
        <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight mb-8 max-w-2xl">
          Pick the door that fits.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to="/artists" data-testid="communities-link-artists-feed" className="group border border-white/10 hover:border-vermilion rounded-sm p-5 transition">
            <Users size={18} className="text-vermilion mb-3" />
            <h4 className="font-display text-base font-black uppercase tracking-tight mb-1">Artist feed</h4>
            <p className="text-xs text-white/55 mb-2">Browse every artist on the platform.</p>
            <span className="text-vermilion text-[11px] font-black uppercase tracking-[0.18em] group-hover:underline">Browse →</span>
          </Link>
          <Link to="/hotspot" data-testid="communities-link-hotspot" className="group border border-white/10 hover:border-vermilion rounded-sm p-5 transition">
            <Radio size={18} className="text-vermilion mb-3" />
            <h4 className="font-display text-base font-black uppercase tracking-tight mb-1">Hotspot</h4>
            <p className="text-xs text-white/55 mb-2">Daily-updated rising artist radar.</p>
            <span className="text-vermilion text-[11px] font-black uppercase tracking-[0.18em] group-hover:underline">Open →</span>
          </Link>
          <Link to="/badge" data-testid="communities-link-badge" className="group border border-white/10 hover:border-vermilion rounded-sm p-5 transition">
            <Sparkles size={18} className="text-vermilion mb-3" />
            <h4 className="font-display text-base font-black uppercase tracking-tight mb-1">Artist badge</h4>
            <p className="text-xs text-white/55 mb-2">Embed your 1o1sound badge anywhere.</p>
            <span className="text-vermilion text-[11px] font-black uppercase tracking-[0.18em] group-hover:underline">Generate →</span>
          </Link>
          <Link to="/jobs" data-testid="communities-link-jobs" className="group border border-white/10 hover:border-vermilion rounded-sm p-5 transition">
            <Headphones size={18} className="text-vermilion mb-3" />
            <h4 className="font-display text-base font-black uppercase tracking-tight mb-1">Work here</h4>
            <p className="text-xs text-white/55 mb-2">Producer & engineer roles open now.</p>
            <span className="text-vermilion text-[11px] font-black uppercase tracking-[0.18em] group-hover:underline">See roles →</span>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 pt-12 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/upload"
            data-testid="communities-bottom-upload"
            className="group block border border-white/10 hover:border-vermilion rounded-sm p-8 transition"
          >
            <Music size={20} className="text-vermilion mb-3" />
            <h3 className="font-display text-2xl font-black uppercase tracking-tight mb-1">Upload your first track</h3>
            <p className="text-sm text-white/55 mb-4">90/10 payouts, no gatekeepers.</p>
            <p className="text-vermilion text-[12px] font-black uppercase tracking-[0.18em] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Start uploading <ArrowRight size={14} />
            </p>
          </Link>
          <Link
            to="/plans"
            data-testid="communities-bottom-plans"
            className="group block border border-white/10 hover:border-vermilion rounded-sm p-8 transition"
          >
            <Flame size={20} className="text-vermilion mb-3" />
            <h3 className="font-display text-2xl font-black uppercase tracking-tight mb-1">Listen on us first</h3>
            <p className="text-sm text-white/55 mb-4">Plans start at $10/mo with 2 months free.</p>
            <p className="text-vermilion text-[12px] font-black uppercase tracking-[0.18em] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              See plans <ArrowRight size={14} />
            </p>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Communities;
